import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import "./candidate.css";
import Spinner from 'components/Spinner';
import { getScreeningsByCandidate, createScreeningDetails, updateScreeningDetails, getActiveProgramList, getPartnerList, updateCandidateCall, getS3PresignedUrl, uploadDocumentBucket, downloadDocumentBucket } from "../../lib/axiosUtils";
import ShowToast from 'components/ShowToast';
import { TYPE_SUCCESS, TYPE_FAIL, INTERVAL, PHONE_NUMBER_PATTERN, emailPattern } from "assets/constants/Constants";
import { calculateAgeFromDate, fetchMetadata, sanitizeAnnualIncome } from 'lib/utils';
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';

const MIN_CANDIDATE_AGE = 18;
const MIN_CANDIDATE_AGE_ERROR = "Candidate must be 18 or older";

const engagementTypeOptions = [
    { label: 'CSR', value: 'CSR' },
    { label: 'RTD', value: 'RTD' }
];

const scoreOptions = [
    { value: '1', label: '1' },
    { value: '2', label: '2' },
    { value: '3', label: '3' },
    { value: '4', label: '4' },
    { value: '5', label: '5' },
    { value: '6', label: '6' },
    { value: '7', label: '7' },
    { value: '8', label: '8' },
    { value: '9', label: '9' },
    { value: '10', label: '10' }
];

const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' }
];

const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
];

const getStateOption = (stateName, stateList = []) => {
    const state = stateList.find((item) => item.state_name === stateName || item.state_id === stateName);
    return state ? { label: state.state_name, value: state.state_id } : null;
};

const getCityOption = (cityName, cityList = []) => {
    const city = cityList.find((item) => item.city_name === cityName || item.city_id === cityName);
    return city ? { label: city.city_name, value: city.city_id } : null;
};

const normalizeCandidateForm = (candidate = {}) => ({
    engagementType: candidate.engagement_type || candidate.engagementType || "",
    partnerId: candidate.partner_id || candidate.partnerId || "",
    trainingPartner: candidate.training_partner || candidate.trainingPartner || "",
    firstName: candidate.first_name || candidate.firstName || "",
    lastName: candidate.last_name || candidate.lastName || "",
    fatherName: candidate.fathers_name || candidate.fatherName || "",
    dobDate: candidate.date_of_birth || candidate.dobDate || "",
    ageNumber: candidate.age ?? candidate.ageNumber ?? "",
    qualification: candidate.qualification || "",
    passingYear: candidate.passing_year || candidate.passingYear || "",
    email: candidate.email || "",
    mobileNumber: candidate.mobile_no || candidate.mobileNumber || "",
    adhaarNo: candidate.adhaar_no || candidate.adhaarNo || "",
    gender: candidate.gender || "",
    state: candidate.state || "",
    stateId: candidate.state_id || "",
    city: candidate.city || "",
    cityId: candidate.city_id || "",
    primaryOccupation: candidate.parent_occupation || candidate.primaryOccupation || "",
    annualIncome: candidate.annual_income ?? candidate.annualIncome ?? "",
    documents: {
        aadhar: candidate.documents?.aadhar || "",
        pan: candidate.documents?.pan || "",
        qualification: candidate.documents?.qualification || ""
    }
});

const createCandidatePayload = async (candidateForm, documentSectionData, candidateId, currentCandidateData) => {
    const uploadedDocumentUrls = {
        aadhar: documentSectionData.aadharUpload ? await uploadDocumentFileToS3("Aadhar Card", documentSectionData.aadharUpload) : currentCandidateData?.documents?.aadhar || "",
        pan: documentSectionData.panUpload ? await uploadDocumentFileToS3("PAN Card", documentSectionData.panUpload) : currentCandidateData?.documents?.pan || "",
        qualification: documentSectionData.qualificationUpload ? await uploadDocumentFileToS3("Qualification Certificate", documentSectionData.qualificationUpload) : currentCandidateData?.documents?.qualification || ""
    };

    return {
        firstName: candidateForm.firstName,
        lastName: candidateForm.lastName,
        fatherName: candidateForm.fatherName,
        dobDate: candidateForm.dobDate,
        ageNumber: candidateForm.ageNumber,
        mobileNumber: candidateForm.mobileNumber,
        adhaarNo: candidateForm.adhaarNo,
        qualification: candidateForm.qualification,
        email: candidateForm.email,
        gender: candidateForm.gender,
        state: candidateForm.state,
        city: candidateForm.city,
        trainingPartner: candidateForm.trainingPartner,
        partnerId: candidateForm.partnerId,
        programId: currentCandidateData?.program_id || "",
        addedFrom: "screening-detail",
        userId: JSON.parse(localStorage.getItem("userData"))?._id,
        primaryOccupation: candidateForm.primaryOccupation,
        annualIncome: sanitizeAnnualIncome(candidateForm.annualIncome),
        engagementType: candidateForm.engagementType,
        passingYear: candidateForm.passingYear,
        documents: {
            aadhar: uploadedDocumentUrls.aadhar,
            pan: uploadedDocumentUrls.pan,
            qualification: uploadedDocumentUrls.qualification
        }
    };
};

const uploadDocumentFileToS3 = async (fieldKey, fileValue) => {
    if (!fileValue) {
        return null;
    }

    let file = fileValue;

    if (typeof file === "string" && /^(https?:\/\/|http:\/\/)/i.test(file)) {
        return file;
    }

    if (typeof file === "string" && file.startsWith("data:")) {
        const mimeType = file.match(/data:(.*);base64/)[1];
        const extension = mimeType.split("/")[1];
        const blob = await fetch(file).then((res) => res.blob());
        file = new File([blob], `${fieldKey.replace(/\s+/g, "_")}.${extension}`, { type: mimeType });
    }

    const response = await getS3PresignedUrl({}, { fileName: file.name, fileType: file.type });
    const { url, fileUrl } = response.data;
    await uploadDocumentBucket({ "Content-Type": file.type }, { url, file });
    return fileUrl;
};

const ScreeningDetail = (props) => {
    const [screeningId, setScreeningId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const userDetails = JSON.parse(localStorage.getItem("userData"));
    const [candidateId] = useState(props.location.state?.candidateId);
    const [partnerId] = useState(props.location.state?.partnerId);
    const [engagementType] = useState(props.location.state?.engagementType);
    const [fullName] = useState(props.location.state?.fullName);
    const [projectOptions, setProjectOptions] = useState([]);
    const [metaData, setMetaData] = useState(JSON.parse(localStorage.getItem("metadata") || "[]"));
    const [partnerOptions, setPartnerOptions] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [cityOptions, setCityOptions] = useState([]);
    const [candidateData, setCandidateData] = useState({});
    const [originalCandidateData, setOriginalCandidateData] = useState(null);
    const [candidateForm, setCandidateForm] = useState(normalizeCandidateForm({}));
    const [candidateErrors, setCandidateErrors] = useState({});
    const [documentSectionData, setDocumentSectionData] = useState({
        aadharUpload: "",
        panUpload: "",
        qualificationUpload: ""
    });
    const [formData, setFormData] = useState({
        screening_date: new Date().toISOString().split("T")[0],
        screener_id: userDetails?._id,
        screener_name: `${userDetails?.first_name || ""} ${userDetails?.last_name || ""}`.trim(),
        partner_id: partnerId,
        engagement_type: engagementType,
        grooming_score: "",
        confidence_score: "",
        communication_score: "",
        knowledge_score: "",
        acceptance_to_relocate: "",
        screener_notes: "",
        screening_status: "",
        assigned_project_id: ""
    });
    const [disabledButtons, setDisabledButtons] = useState(false);
    const [disabledProjectSelection, setDisabledProjectSelection] = useState(false);

    useEffect(() => {
        const fetchMetaData = async () => {
            if (metaData.length === 0) {
                const result = await fetchMetadata();
                localStorage.setItem("metadata", JSON.stringify(result || []));
                setMetaData(result || []);
            }
        };

        fetchMetaData();
    }, [metaData.length]);

    useEffect(() => {
        if (metaData?.length) {
            const states = metaData[0]?.meta_data?.states || [];
            const cities = metaData[0]?.meta_data?.cities || [];
            const stateList = states.map((item) => ({ label: item.state_name, value: item.state_id }));
            setStateOptions(stateList);

            const selectedStateId = candidateForm.stateId || states.find((item) => item.state_name === candidateForm.state)?.state_id;
            const filteredCities = selectedStateId
                ? cities.filter((item) => item.state_id === selectedStateId)
                : cities;
            setCityOptions(filteredCities.map((item) => ({ label: item.city_name, value: item.city_id })));
        }
    }, [metaData, candidateForm.state, candidateForm.stateId]);

    useEffect(() => {
        const fetchPartnerList = async () => {
            try {
                const response = await getPartnerList();
                const listComponent = response?.data?.result?.map((item) => ({
                    label: item.training_partner,
                    value: item._id
                })) || [];
                setPartnerOptions(listComponent);
            } catch (error) {
                console.error("Error while fetching partner list", error);
            }
        };

        fetchPartnerList();
    }, []);

    useEffect(() => {
        if (!candidateId) return;
        const fetchScreeningDetails = async () => {
            try {
                setIsLoading(true);
                const screenData = await getScreeningsByCandidate({}, {}, candidateId);
                const screeningList = screenData?.data?.result.screenings || [];
                const candidateResult = screenData?.data?.result.candidate || {};
                const normalizedCandidate = normalizeCandidateForm(candidateResult);
                const metadataStates = metaData[0]?.meta_data?.states || [];
                const metadataCities = metaData[0]?.meta_data?.cities || [];
                const derivedStateId = normalizedCandidate.stateId || metadataStates.find((item) => item.state_name === normalizedCandidate.state)?.state_id || "";
                const derivedCityId = normalizedCandidate.cityId || metadataCities.find((item) => item.city_name === normalizedCandidate.city)?.city_id || "";

                setCandidateData(candidateResult);
                setOriginalCandidateData({
                    ...normalizedCandidate,
                    stateId: derivedStateId,
                    cityId: derivedCityId
                });
                setCandidateForm({
                    ...normalizedCandidate,
                    stateId: derivedStateId,
                    cityId: derivedCityId
                });
                setDocumentSectionData({
                    aadharUpload: candidateResult?.documents?.aadhar || "",
                    panUpload: candidateResult?.documents?.pan || "",
                    qualificationUpload: candidateResult?.documents?.qualification || ""
                });

                if (screeningList.length > 0) {
                    const screening = screeningList[0];
                    if (screening.screening_status === "Selected") {
                        setDisabledButtons(true);
                    }

                    if (screening.screening_status === "Selected" && screening.assigned_project_id) {
                        setDisabledProjectSelection(true);
                    }

                    setScreeningId(screening._id);
                    setFormData({
                        screening_date: screening.screening_date ? new Date(screening.screening_date).toISOString().split("T")[0] : formData.screening_date,
                        screener_id: screening.screener_id || "",
                        screener_name: screening.screener_name ? screening.screener_name : formData.screener_name,
                        grooming_score: screening.grooming_score || "",
                        confidence_score: screening.confidence_score || "",
                        communication_score: screening.communication_score || "",
                        knowledge_score: screening.knowledge_score || "",
                        acceptance_to_relocate: screening.acceptance_to_relocate || "",
                        screener_notes: screening.screener_notes || "",
                        screening_status: screening.screening_status || "",
                        assigned_project_id: screening.assigned_project_id || ""
                    });
                }

            } catch (error) {
                console.error("Fetch Screening Details Error:", error);
            } finally {
                setIsLoading(false);
            }
        };

        const getAllprojectList = async () => {
            const programList = await getActiveProgramList({}, {});
            const programs = programList?.data?.result || [];
            setProjectOptions(
                programs.map((program) => ({
                    label: program.program_name,
                    value: program._id
                }))
            );
        };

        fetchScreeningDetails();
        getAllprojectList();
        // eslint-disable-next-line
    }, [candidateId]);

    const getCandidateChangeSnapshot = () => ({
        ...candidateForm,
        documents: {
            aadhar: documentSectionData.aadharUpload || candidateForm.documents.aadhar || "",
            pan: documentSectionData.panUpload || candidateForm.documents.pan || "",
            qualification: documentSectionData.qualificationUpload || candidateForm.documents.qualification || ""
        }
    });

    const hasCandidateDataChanged = () => {
        if (!originalCandidateData) return false;
        return JSON.stringify(getCandidateChangeSnapshot()) !== JSON.stringify({
            ...originalCandidateData,
            documents: {
                aadhar: originalCandidateData.documents?.aadhar || "",
                pan: originalCandidateData.documents?.pan || "",
                qualification: originalCandidateData.documents?.qualification || ""
            }
        });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value
        }));
    };

    const handleSelectChange = (selectedOption, fieldName) => {
        setFormData((prevData) => ({
            ...prevData,
            [fieldName]: selectedOption ? selectedOption.value : ""
        }));
    };

    const handleCandidateInputChange = (fieldName, value) => {
        setCandidateForm((prevData) => ({
            ...prevData,
            [fieldName]: value
        }));
    };

    const handleStateSelection = (selected) => {
        const stateList = metaData[0]?.meta_data?.states || [];
        const selectedState = stateList.find((item) => item.state_id === selected?.value) || null;
        const nextStateId = selectedState ? selectedState.state_id : "";
        const nextStateName = selectedState ? selectedState.state_name : "";

        setCandidateForm((prevData) => ({
            ...prevData,
            state: nextStateName,
            stateId: nextStateId,
            city: "",
            cityId: ""
        }));
    };

    const handleCitySelection = (selected) => {
        const cityList = (metaData[0]?.meta_data?.cities || []).filter((item) => item.state_id === candidateForm.stateId);
        const selectedCity = cityList.find((item) => item.city_id === selected?.value) || null;

        setCandidateForm((prevData) => ({
            ...prevData,
            city: selectedCity ? selectedCity.city_name : "",
            cityId: selected ? selected.value : ""
        }));
    };

    const handleCandidateDateChange = (date) => {
        const formattedDate = date ? new Date(date).toISOString().split("T")[0] : "";
        setCandidateForm((prevData) => ({
            ...prevData,
            dobDate: formattedDate,
            ageNumber: formattedDate ? calculateAgeFromDate(date) : ""
        }));
    };

    const handleDocumentFileChange = (key, event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setDocumentSectionData((prev) => ({
                ...prev,
                [key]: reader.result
            }));
        };
        reader.readAsDataURL(file);
    };

    const getDocumentFileName = (value) => {
        if (typeof value !== "string" || !value) {
            return "";
        }
        return value.split("/").pop() || "document";
    };

    const isDownloadableDocument = (value) => typeof value === "string" && /^https?:\/\//i.test(value);

    const handleDocumentDownload = async (documentUrl, label) => {
        if (!documentUrl) {
            return;
        }
        if (documentUrl.startsWith("data:")) {
            window.open(documentUrl, "_blank", "noopener,noreferrer");
            return;
        }

        const s3KeyMatch = documentUrl.match(/^https:\/\/[^/]+\.s3\.amazonaws\.com\/(.+)$/i);
        const fileKey = s3KeyMatch ? s3KeyMatch[1] : documentUrl;

        try {
            const response = await downloadDocumentBucket({}, { fileKey });
            const downloadUrl = response?.data?.downloadUrl || documentUrl;
            window.open(downloadUrl, "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error(`Failed to download ${label}:`, error);
            if (/^https?:\/\//i.test(documentUrl)) {
                window.open(documentUrl, "_blank", "noopener,noreferrer");
            }
        }
    };

    const validateCandidateForm = () => {
        let isValid = true;
        let error = {};

        if (!candidateForm.engagementType) {
            isValid = false;
            error.engagementTypeError = "Please select engagement type";
        }

        if (!candidateForm.firstName) {
            isValid = false;
            error.firstNameError = "Please enter first name";
        }

        if (!candidateForm.lastName) {
            isValid = false;
            error.lastNameError = "Please enter last name";
        }

        if (!candidateForm.fatherName) {
            isValid = false;
            error.fatherNameError = "Please enter father name";
        }

        if (!candidateForm.dobDate) {
            isValid = false;
            error.dobDateError = "Please select date of birth";
        }

        if (candidateForm.ageNumber === "" || candidateForm.ageNumber === null || candidateForm.ageNumber === undefined) {
            isValid = false;
            error.ageNumberError = "Please select date of birth";
        } else if (Number(candidateForm.ageNumber) < MIN_CANDIDATE_AGE) {
            isValid = false;
            error.ageNumberError = MIN_CANDIDATE_AGE_ERROR;
        }

        if (!candidateForm.gender) {
            isValid = false;
            error.genderError = "Please select gender";
        }

        if (candidateForm.email) {
            if (!emailPattern.test(candidateForm.email)) {
                isValid = false;
                error.emailIdError = "Please enter a valid email address.";
            }
        } else {
            isValid = false;
            error.emailIdError = "Please enter an email address";
        }

        if (candidateForm.mobileNumber) {
            if (!PHONE_NUMBER_PATTERN.test(candidateForm.mobileNumber)) {
                isValid = false;
                error.MobileNoError = "Please enter a valid mobile number.";
            }
        } else {
            isValid = false;
            error.MobileNoError = "Please enter a mobile number";
        }

        if (!candidateForm.qualification) {
            isValid = false;
            error.qualificationsError = "Please select qualification";
        }

        if (!candidateForm.passingYear) {
            isValid = false;
            error.passingYearError = "Please enter year of qualification";
        }

        if (!candidateForm.state) {
            isValid = false;
            error.stateError = "Please enter state";
        }

        if (!candidateForm.city) {
            isValid = false;
            error.cityError = "Please enter city";
        }

        if (!candidateForm.adhaarNo) {
            isValid = false;
            error.aadharNumberError = "Please enter Aadhaar number";
        }

        if (!candidateForm.primaryOccupation) {
            isValid = false;
            error.primaryOccupationError = "Please enter primary occupation";
        }

        if (!candidateForm.annualIncome && candidateForm.annualIncome !== 0) {
            isValid = false;
            error.annualIncomeError = "Please enter annual income";
        }

        const currentAadhar = documentSectionData.aadharUpload || candidateForm.documents.aadhar || "";
        const currentQualification = documentSectionData.qualificationUpload || candidateForm.documents.qualification || "";

        if (!currentAadhar) {
            isValid = false;
            error.aadharUploadError = "Please upload aadhar card";
        }

        if (!currentQualification) {
            isValid = false;
            error.qualificationUploadError = "Please upload qualification certificate";
        }

        setCandidateErrors(error);
        return isValid;
    };

    const createScreeningHandler = async (e) => {
        e.preventDefault();

        try {
            setIsLoading(true);

            if (hasCandidateDataChanged()) {
                const isCandidateValid = validateCandidateForm();
                if (!isCandidateValid) {
                    return;
                }

                const candidateUpdatePayload = await createCandidatePayload(candidateForm, documentSectionData, candidateId, candidateData);
                const candidateUpdateResponse = await updateCandidateCall({}, candidateUpdatePayload, candidateId);
                console.log("candidateUpdateResponse:", candidateUpdateResponse);
                if (!(candidateUpdateResponse && candidateUpdateResponse.statusCode === 200)) {
                    ShowToast('Candidate update failed, please try again', TYPE_FAIL, INTERVAL, "", "");
                    return;
                }
                setOriginalCandidateData({
                    ...candidateForm,
                    documents: {
                        aadhar: documentSectionData.aadharUpload || candidateForm.documents.aadhar || "",
                        pan: documentSectionData.panUpload || candidateForm.documents.pan || "",
                        qualification: documentSectionData.qualificationUpload || candidateForm.documents.qualification || ""
                    }
                });
            }

            const payload = {
                candidate_id: candidateId,
                screening_date: new Date().toISOString().split("T")[0],
                screener_id: userDetails?._id,
                partner_id: partnerId ? partnerId : null,
                grooming_score: Number(formData.grooming_score),
                confidence_score: Number(formData.confidence_score),
                communication_score: Number(formData.communication_score),
                knowledge_score: Number(formData.knowledge_score),
                acceptance_to_relocate: formData.acceptance_to_relocate,
                screener_notes: formData.screener_notes,
                screening_status: formData.screening_status,
                assigned_project_id: formData.assigned_project_id || null,
                engagement_type: candidateForm.engagementType || engagementType || ""
            };

            let response;

            if (screeningId) {
                response = await updateScreeningDetails({}, payload, screeningId);
                console.log("update response:", response);
                ShowToast('Screening data updated Successfully', TYPE_SUCCESS, INTERVAL, props, "/tjp-screening-queue");
            } else {
                response = await createScreeningDetails({}, payload);
                console.log("create response:", response);
                ShowToast('Screening data created Successfully', TYPE_SUCCESS, INTERVAL, props, "/tjp-screening-queue");
            }
        } catch (error) {
            console.error("Screening Submit Error:", error);
            ShowToast('Screening Submit Error, Please try again', TYPE_FAIL, INTERVAL, "", "");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            {isLoading ? (
                <Spinner />
            ) : (
                <div className="main-content">
                    <div className="back">
                        <ul className="breadcrumb1">
                            <li><a href="/tjp-screening-queue">Screening queue</a></li>
                            <li>Screening detail - {fullName}</li>
                        </ul>
                    </div>
                    <div className="add-user-wrapper">
                        <div className="mx-auto text-left border create-user-div">
                            <form onSubmit={createScreeningHandler} className="pt-1">
                                <div className="screening-details-div">
                                    <label className="screening-line-div">Candidate's Details</label>
                                    <div className="screening-section-div"></div>
                                </div>
                                <div className="row mt-4">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Engagement type <span className="text-danger">*</span></label>
                                        <Select
                                            name="engagementType"
                                            placeholder="Select Engagement Type"
                                            value={engagementTypeOptions.find((option) => option.value === candidateForm.engagementType) || null}
                                            options={engagementTypeOptions}
                                            onChange={(selected) => handleCandidateInputChange("engagementType", selected ? selected.value : "")}
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.engagementTypeError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Training Partner</label>
                                        <Select
                                            name="trainingPartner"
                                            placeholder="Select Training Partner"
                                            value={partnerOptions.find((option) => option.value === candidateForm.partnerId) || null}
                                            options={partnerOptions}
                                            onChange={(selected) => {
                                                setCandidateForm((prevData) => ({
                                                    ...prevData,
                                                    partnerId: selected ? selected.value : "",
                                                    trainingPartner: selected ? selected.label : ""
                                                }));
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">First Name <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.firstName} onChange={(e) => handleCandidateInputChange("firstName", e.target.value)} placeholder="Enter First Name" />
                                        <div className="text-danger error-msg">{candidateErrors.firstNameError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Last Name <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.lastName} onChange={(e) => handleCandidateInputChange("lastName", e.target.value)} placeholder="Enter Last Name" />
                                        <div className="text-danger error-msg">{candidateErrors.lastNameError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Father Name <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.fatherName} onChange={(e) => handleCandidateInputChange("fatherName", e.target.value)} placeholder="Enter Father Name" />
                                        <div className="text-danger error-msg">{candidateErrors.fatherNameError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Gender <span className="text-danger">*</span></label>
                                        <Select
                                            name="gender"
                                            placeholder="Select Gender"
                                            value={genderOptions.find((option) => option.value === candidateForm.gender) || null}
                                            options={genderOptions}
                                            onChange={(selected) => handleCandidateInputChange("gender", selected ? selected.value : "")}
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.genderError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Date of Birth <span className="text-danger">*</span></label>
                                        <DatePicker
                                            className="form-control brand-font p-3 placeholderColor add-user-input"
                                            selected={candidateForm.dobDate ? new Date(candidateForm.dobDate) : null}
                                            onChange={handleCandidateDateChange}
                                            placeholderText="Select Date of Birth"
                                            dateFormat="dd/MM/yyyy"
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.dobDateError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Age <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.ageNumber} readOnly placeholder="Age auto-calculates from DOB" />
                                        <div className="text-danger error-msg">{candidateErrors.ageNumberError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Highest Qualification <span className="text-danger">*</span></label>
                                        <Select
                                            name="qualification"
                                            placeholder="Select Qualification"
                                            value={
                                                (metaData[0]?.meta_data?.qualifications || []).find(
                                                    (item) => item.education_name === candidateForm.qualification
                                                ) ? {
                                                    label: candidateForm.qualification,
                                                    value: candidateForm.qualification
                                                } : null
                                            }
                                            options={(metaData[0]?.meta_data?.qualifications || []).map((item) => ({
                                                label: item.education_name,
                                                value: item.education_name
                                            }))}
                                            onChange={(selected) => handleCandidateInputChange("qualification", selected ? selected.value : "")}
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.qualificationsError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Year of Qualification <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.passingYear} onChange={(e) => handleCandidateInputChange("passingYear", e.target.value)} placeholder="Enter Year of Qualification" />
                                        <div className="text-danger error-msg">{candidateErrors.passingYearError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6 email-input">
                                        <label className="form-label">Email <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.email} onChange={(e) => handleCandidateInputChange("email", e.target.value)} placeholder="Enter Email" />
                                        <div className="text-danger error-msg">{candidateErrors.emailIdError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Mobile <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.mobileNumber} onChange={(e) => {
                                            if (!isNaN(e.target.value)) {
                                                handleCandidateInputChange("mobileNumber", e.target.value);
                                            }
                                        }} placeholder="Enter Mobile Number" />
                                        <div className="text-danger error-msg">{candidateErrors.MobileNoError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Aadhar Number <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" value={candidateForm.adhaarNo} onChange={(e) => {
                                            if (!isNaN(e.target.value)) {
                                                handleCandidateInputChange("adhaarNo", e.target.value);
                                            }
                                        }} placeholder="Enter Aadhar No." />
                                        <div className="text-danger error-msg">{candidateErrors.aadharNumberError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Select State <span className="text-danger">*</span></label>
                                        <Select
                                            name="state"
                                            placeholder="Select State"
                                            value={getStateOption(candidateForm.state, metaData[0]?.meta_data?.states || [])}
                                            options={stateOptions}
                                            onChange={handleStateSelection}
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.stateError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Select Candidate Home city <span className="text-danger">*</span></label>
                                        <Select
                                            name="city"
                                            placeholder="Select City"
                                            value={getCityOption(candidateForm.city, (metaData[0]?.meta_data?.cities || []).filter((item) => item.state_id === candidateForm.stateId))}
                                            options={cityOptions}
                                            onChange={handleCitySelection}
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.cityError}</div>
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Primary occupation of the earning parent <span className="text-danger">*</span></label>
                                        <input className="form-control brand-font p-3 placeholderColor add-user-input" type="text" value={candidateForm.primaryOccupation} onChange={(e) => handleCandidateInputChange("primaryOccupation", e.target.value)} placeholder="Enter Primary Occupation" />
                                        <div className="text-danger error-msg">{candidateErrors.primaryOccupationError}</div>
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Annual household income <span className="text-danger">*</span></label>
                                        <input
                                            className="form-control brand-font p-3 placeholderColor add-user-input"
                                            type="text"
                                            inputMode="numeric"
                                            value={candidateForm.annualIncome || ""}
                                            onChange={(e) => handleCandidateInputChange("annualIncome", e.target.value.replace(/[^\d,Rs₹.\s-]/gi, "").replace(/(?!^)-/g, "").trim())}
                                            placeholder="Enter Annual Household Income"
                                        />
                                        <div className="text-danger error-msg">{candidateErrors.annualIncomeError}</div>
                                    </div>
                                </div>
                                <div className="mt-4 document-box">
                                    <h6 className="mb-3" style={{ fontWeight: 600, color: '#2f2f2f' }}>Documents</h6>
                                    <div className="row">
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>Aadhar Card <span className="text-danger">*</span></label>
                                            {isDownloadableDocument(documentSectionData.aadharUpload || candidateForm.documents.aadhar) && (
                                                <div className="mb-2">
                                                    <button type="button" className="download-link-button" onClick={() => handleDocumentDownload(documentSectionData.aadharUpload || candidateForm.documents.aadhar, "Aadhar Card")}>View</button>
                                                    <div className="text-muted small mt-1">{getDocumentFileName(documentSectionData.aadharUpload || candidateForm.documents.aadhar)}</div>
                                                </div>
                                            )}
                                            <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="form-control" onChange={(event) => handleDocumentFileChange("aadharUpload", event)} />
                                            <div className="text-danger error-msg">{candidateErrors.aadharUploadError}</div>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>PAN Card</label>
                                            {isDownloadableDocument(documentSectionData.panUpload || candidateForm.documents.pan) && (
                                                <div className="mb-2">
                                                    <button type="button" className="download-link-button" onClick={() => handleDocumentDownload(documentSectionData.panUpload || candidateForm.documents.pan, "PAN Card")}>View</button>
                                                    <div className="text-muted small mt-1">{getDocumentFileName(documentSectionData.panUpload || candidateForm.documents.pan)}</div>
                                                </div>
                                            )}
                                            <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="form-control" onChange={(event) => handleDocumentFileChange("panUpload", event)} />
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label className="form-label" style={{ display: 'block', marginBottom: 8 }}>Qualification Certificate <span className="text-danger">*</span></label>
                                            {isDownloadableDocument(documentSectionData.qualificationUpload || candidateForm.documents.qualification) && (
                                                <div className="mb-2">
                                                    <button type="button" className="download-link-button" onClick={() => handleDocumentDownload(documentSectionData.qualificationUpload || candidateForm.documents.qualification, "Qualification Certificate")}>View</button>
                                                    <div className="text-muted small mt-1">{getDocumentFileName(documentSectionData.qualificationUpload || candidateForm.documents.qualification)}</div>
                                                </div>
                                            )}
                                            <input type="file" accept=".pdf,.png,.jpg,.jpeg" className="form-control" onChange={(event) => handleDocumentFileChange("qualificationUpload", event)} />
                                            <div className="text-danger error-msg">{candidateErrors.qualificationUploadError}</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="screening-details-div mt-4">
                                    <label className="screening-line-div">Screening Details</label>
                                    <div className="screening-section-div"></div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Date of screening </label>
                                        <input type="date" name="screening_date" value={formData.screening_date} className="form-control brand-font p-3 placeholderColor add-user-input" readOnly />
                                    </div>
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Screener</label>
                                        <input type="text" name="screener_name" value={formData.screener_name} className="form-control brand-font p-3 placeholderColor add-user-input" placeholder="Screener Name" />
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="input-label-group col-md-6">
                                        <label className="form-label">Grooming</label>
                                        <Select className="add-user-input-height" placeholder="Select Score" options={scoreOptions} value={scoreOptions.find((option) => option.value === String(formData.grooming_score)) || null} onChange={(selectedOption) => handleSelectChange(selectedOption, "grooming_score")} />
                                    </div>
                                    <div className="input-label-group col-md-6 email-input">
                                        <label className="form-label">Confidence</label>
                                        <Select className="add-user-input-height" placeholder="Select Score" options={scoreOptions} value={scoreOptions.find((option) => option.value === String(formData.confidence_score)) || null} onChange={(selectedOption) => handleSelectChange(selectedOption, "confidence_score")} />
                                    </div>
                                </div>
                                <div className="row mt-0">
                                    <div className="col-md-6">
                                        <label className="form-label">Communication skills</label>
                                        <Select className="add-user-input-height" placeholder="Select Score" options={scoreOptions} value={scoreOptions.find((option) => option.value === String(formData.communication_score)) || null} onChange={(selectedOption) => handleSelectChange(selectedOption, "communication_score")} />
                                    </div>
                                    <div className="col-md-6">
                                        <label className="form-label">Knowledge</label>
                                        <Select className="add-user-input-height" placeholder="Select Score" options={scoreOptions} value={scoreOptions.find((option) => option.value === String(formData.knowledge_score)) || null} onChange={(selectedOption) => handleSelectChange(selectedOption, "knowledge_score")} />
                                    </div>
                                </div>
                                <div className="row mt-0 mt-4">
                                    <div className="col-md-6">
                                        <label className="form-label">Acceptance to relocate</label>
                                        <div className="form-wrap-inner">
                                            <Select placeholder="Select Yes/No" options={yesNoOptions} value={yesNoOptions.find((option) => option.value === formData.acceptance_to_relocate) || null} onChange={(selectedOption) => handleSelectChange(selectedOption, "acceptance_to_relocate")} />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-0 mt-4">
                                    <div className="col-md-12">
                                        <div className="form-wrap">
                                            <label className="form-label">Screener notes</label>
                                            <input name="screener_notes" value={formData.screener_notes} onChange={handleInputChange} className="form-control brand-font p-3 placeholderColor add-user-input" placeholder="Free-text observations" />
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-0 mt-4 mb-5 display-inline">
                                    <div className="form-button-div-screening">
                                        <button type="button" disabled={disabledButtons} className={formData.screening_status === "Selected" ? "brand-button" : "cancel_button"} onClick={() => setFormData((prevData) => ({ ...prevData, screening_status: "Selected" }))}>Selected</button>
                                        <button type="button" disabled={disabledButtons} className={formData.screening_status === "Not selected" ? "brand-button ml-5" : "cancel_button ml-5"} onClick={() => setFormData((prevData) => ({ ...prevData, screening_status: "Not selected", assigned_project_id: "" }))}>Not Selected</button>
                                    </div>
                                </div>
                                {formData.screening_status === "Selected" && (
                                    <div className="row mt-4">
                                        <div className="col-md-6">
                                            <label>Assign Project</label>
                                            <Select name="assigned_project_id" options={projectOptions} isDisabled={disabledProjectSelection} value={projectOptions.find((option) => option.value === formData.assigned_project_id) || null} onChange={(selectedOption) => handleSelectChange(selectedOption, "assigned_project_id")} placeholder="Select Project" />
                                        </div>
                                    </div>
                                )}
                                <div className="row mt-4 mb-4">
                                    <div className="col-md-12">
                                        <button type="submit" className="brand-button" disabled={isLoading}>{isLoading ? "Saving..." : screeningId ? "Update" : "Save"}</button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="row dummy_row"></div>
                </div>
            )}
        </>
    );
};

export default ScreeningDetail;